/**
 * SSE frame parsing for /api/ai/explain and /api/ai/rag.
 *
 * The backend emits `data: {json}\n\n` frames with these event shapes
 * (see graphmind/gateway/schemas.py StreamEvent + the `saved`, `retrieval` and
 * `citations` frames from AIService). Six kinds in all — `retrieval` and
 * `citations` ride the /api/ai/rag stream only, and no studio surface reads
 * either one yet.
 */

export type StreamEvent =
  | { type: "delta"; text: string }
  | {
      type: "done";
      model_id?: string;
      usage_cost_usd?: number;
      conversation_id?: string;
    }
  | { type: "error"; error?: string; model_id?: string }
  | { type: "saved"; node_id: string }
  /** RAG only, emitted BEFORE the model stream starts: the ContextRetriever's
   * self-assessment of what it selected. `report` is a ContextReport and each
   * entry of `documents` a SelectedDocument, both server-side Pydantic models
   * that no generated type covers, so they stay opaque here. */
  | {
      type: "retrieval";
      report: Record<string, unknown>;
      documents: Record<string, unknown>[];
    }
  /** RAG only, emitted AFTER `done`: the distinct `[node:<id>]` tags in the
   * answer that resolved to a real source block, and a count of the ones that
   * did not (hallucinated ids, dropped from this list but still inline in the
   * answer text). */
  | {
      type: "citations";
      citations: { node_id: string; document_id: string; label: string }[];
      dropped: number;
    };

/**
 * Incremental SSE parser. Feed it raw chunks (any split points — frames may
 * span chunks); it returns fully-parsed events as they complete.
 */
export class SseParser {
  private buffer = "";

  push(chunk: string): StreamEvent[] {
    this.buffer += chunk;
    const events: StreamEvent[] = [];
    let sep: number;
    // Frames are terminated by a blank line. Tolerate \r\n from proxies.
    while ((sep = this.buffer.search(/\r?\n\r?\n/)) !== -1) {
      const frame = this.buffer.slice(0, sep);
      this.buffer = this.buffer.slice(sep).replace(/^\r?\n\r?\n/, "");
      for (const line of frame.split(/\r?\n/)) {
        if (!line.startsWith("data: ")) continue; // ignore comments/other fields
        try {
          events.push(JSON.parse(line.slice(6)) as StreamEvent);
        } catch {
          // Malformed frame: surface as an error event rather than throwing
          // mid-stream — the caller decides whether to abort.
          events.push({ type: "error", error: `unparseable frame: ${line.slice(6, 80)}` });
        }
      }
    }
    return events;
  }
}

/** Read a fetch() Response body as a stream of parsed SSE events. */
export async function* readSseStream(
  response: Response,
): AsyncGenerator<StreamEvent> {
  if (!response.ok) {
    throw new Error(`stream request failed: ${response.status}`);
  }
  if (!response.body) {
    throw new Error("response has no body (SSE requires a streaming fetch)");
  }
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  const parser = new SseParser();
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      for (const event of parser.push(decoder.decode(value, { stream: true }))) {
        yield event;
      }
    }
  } finally {
    reader.releaseLock();
  }
}
